import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Target, Lightbulb, Users, ArrowRight } from 'lucide-react';
import '../styles/About.css';

const About = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: 'Product-First Mindset',
      description: 'We build products that solve real problems, not just code for the sake of code.'
    },
    {
      icon: <Lightbulb size={32} />,
      title: 'Innovation & Excellence',
      description: 'Staying ahead with the latest technologies while maintaining quality standards.'
    },
    {
      icon: <Users size={32} />,
      title: 'Client Partnership',
      description: 'Your success is our success. We work as an extension of your team.'
    }
  ];

  const process = [
    {
      phase: 'Discover',
      description:
        'We begin by deeply understanding your business goals, users, and technical needs. Through discovery workshops, research, and analysis, we define the product vision and success metrics.'
    },
    {
      phase: 'Prototype',
      description:
        'We rapidly build and test prototypes to validate ideas early. Interactive wireframes help refine the user experience before full development.'
    },
    {
      phase: 'Build',
      description:
        'Using agile sprints, we develop scalable, high-quality software. You receive continuous updates, regular builds, and transparent progress.'
    },
    {
      phase: 'Grow',
      description:
        'After launch, we optimize using analytics, user feedback, and performance insights. We help you scale with new features and improvements.'
    }
  ];

  return (
    <div className="about-page">
      <Navbar />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1 className="page-title">About BigSoftTech</h1>
            <p className="page-subtitle">
              We're a product-focused engineering team building world-class digital products.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2 className="section-title">Our Story</h2>
              <div className="story-paragraphs">
                <p>
                  BigSoftTech was founded to help businesses build better products—faster and smarter.
                  We work with startups, SMEs, and enterprises to bring ideas to life through
                  world-class engineering and design.
                </p>
                <p>
                  Our product-first mindset sets us apart. We don’t just write code—we solve
                  problems. Every decision is user-centered, data-driven, and purpose-focused.
                </p>
                <p>
                  With expertise across mobile, web, cloud, and AI/ML, we are your full-stack
                  partner from concept to scale.
                </p>
              </div>
            </div>

            <div className="story-image">
              <img
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=800"
                alt="Team collaboration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Values</h2>
            <p className="section-subtitle">The principles that guide everything we do</p>
          </div>

          <div className="values-grid">
            {values.map((value, index) => (
              <div className="value-card card" key={index}>
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="about-process">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How We Work</h2>
            <p className="section-subtitle">Our proven 4-phase approach to product development</p>
          </div>

          <div className="process-timeline">
            {process.map((step, index) => (
              <div className="timeline-item" key={index}>
                <div className="timeline-marker">
                  <span className="timeline-number">{index + 1}</span>
                </div>
                <div className="timeline-content">
                  <h3>{step.phase}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to work with us?</h2>
            <p>Let’s discuss your project and see how we can help you grow.</p>

            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">
                Get in Touch
                <ArrowRight size={20} />
              </Link>

              <Link to="/work" className="btn btn-secondary btn-large">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      
    </div>
  );
};

export default About;
